import { applyRulesToTransaction } from './bankTransactionRuleApplier';
import type { TransactionToCategorize } from './bankTransactionRuleApplier';

export type { TransactionToCategorize };
import { classifyTransactionML, type TransactionFeatures, type ClassificationResult as MLResult } from './mlTransactionClassifier';
import { 
  getCalibrator, 
  determineAction, 
  type ConfidenceThresholds,
  DEFAULT_THRESHOLDS 
} from './confidenceCalibration';
import { classifyByLearnedVendor } from './feedbackLearningSystem';

export interface ClassificationResult {
  category_type: 'revenue' | 'expenses' | 'balance_sheet' | null;
  subcategory: string | null;
  detail_source: string | null;
  /** Raw confidence from classifier */
  confidence: number;
  /** Calibrated confidence (reflects actual accuracy) */
  calibratedConfidence?: number;
  method: 'rule' | 'ml' | 'hybrid' | 'historical' | 'learned_vendor' | 'none';
  needsReview: boolean;
  /** Recommended action based on calibrated confidence */
  recommendedAction?: 'auto_approve' | 'high_confidence' | 'needs_review' | 'manual_required';
  matchReason?: string;
  similarTransactions?: Array<{
    id: string;
    description: string;
    similarity: number;
  }>;
}

/**
 * Combines rule-based and ML-based classification results
 */
function combineClassifications(
  ruleResult: { rule: any; confidence: number; matchReason: string } | null,
  mlResult: MLResult
): ClassificationResult {
  // If we have a high-confidence rule match, use it
  if (ruleResult && ruleResult.confidence >= 0.8) {
    return {
      category_type: ruleResult.rule.category_type,
      subcategory: ruleResult.rule.subcategory,
      detail_source: ruleResult.rule.detail_source || null,
      confidence: ruleResult.confidence,
      method: 'rule',
      needsReview: ruleResult.confidence < 0.9,
      matchReason: ruleResult.matchReason,
    };
  }
  
  // If we have a high-confidence ML match, use it
  if (mlResult.confidence >= 0.8 && mlResult.category_type) {
    return {
      category_type: mlResult.category_type,
      subcategory: mlResult.subcategory,
      detail_source: mlResult.detail_source,
      confidence: mlResult.confidence,
      method: 'ml',
      needsReview: mlResult.confidence < 0.9,
      similarTransactions: mlResult.similarTransactions,
    };
  }
  
  // Hybrid approach: combine rule and ML results
  if (ruleResult && mlResult.category_type) {
    // Check if they agree
    const agree = ruleResult.rule.category_type === mlResult.category_type &&
                  ruleResult.rule.subcategory === mlResult.subcategory;
    
    if (agree) {
      // They agree - boost confidence
      const combinedConfidence = Math.min(
        (ruleResult.confidence * 0.5 + mlResult.confidence * 0.5) * 1.2,
        0.95
      );
      
      return {
        category_type: ruleResult.rule.category_type,
        subcategory: ruleResult.rule.subcategory,
        detail_source: ruleResult.rule.detail_source || mlResult.detail_source || null,
        confidence: combinedConfidence,
        method: 'hybrid',
        needsReview: combinedConfidence < 0.85,
        matchReason: `Rule + ML agreement: ${ruleResult.matchReason}`,
        similarTransactions: mlResult.similarTransactions,
      };
    } else {
      // They disagree - use the higher confidence one
      if (ruleResult.confidence > mlResult.confidence) {
        return {
          category_type: ruleResult.rule.category_type,
          subcategory: ruleResult.rule.subcategory,
          detail_source: ruleResult.rule.detail_source || null,
          confidence: ruleResult.confidence * 0.9, // Reduce confidence due to disagreement
          method: 'rule',
          needsReview: true, // Always review when they disagree
          matchReason: ruleResult.matchReason,
        };
      } else {
        return {
          category_type: mlResult.category_type,
          subcategory: mlResult.subcategory,
          detail_source: mlResult.detail_source,
          confidence: mlResult.confidence * 0.9, // Reduce confidence due to disagreement
          method: 'ml',
          needsReview: true,
          similarTransactions: mlResult.similarTransactions,
        };
      }
    }
  }
  
  // Use rule result if available (even if low confidence)
  if (ruleResult) {
    return {
      category_type: ruleResult.rule.category_type,
      subcategory: ruleResult.rule.subcategory,
      detail_source: ruleResult.rule.detail_source || null,
      confidence: ruleResult.confidence,
      method: 'rule',
      needsReview: ruleResult.confidence < 0.8,
      matchReason: ruleResult.matchReason,
    };
  }
  
  // Use ML result if available (even if low confidence)
  if (mlResult.category_type) {
    return {
      category_type: mlResult.category_type,
      subcategory: mlResult.subcategory,
      detail_source: mlResult.detail_source,
      confidence: mlResult.confidence,
      method: 'ml',
      needsReview: mlResult.confidence < 0.8,
      similarTransactions: mlResult.similarTransactions,
    };
  }
  
  // No classification found
  return {
    category_type: null,
    subcategory: null,
    detail_source: null,
    confidence: 0,
    method: 'none',
    needsReview: true,
  };
}

/**
 * Classifies a single transaction using both rule-based and ML approaches
 * 
 * Enhanced with:
 * - Learned vendor classification
 * - Confidence calibration
 * - Action recommendations
 */
export async function classifyTransaction(
  transaction: TransactionToCategorize,
  options: {
    useCalibration?: boolean;
    useLearnedVendors?: boolean;
    thresholds?: ConfidenceThresholds;
  } = {}
): Promise<ClassificationResult> {
  const {
    useCalibration = true,
    useLearnedVendors = true,
    thresholds = DEFAULT_THRESHOLDS
  } = options;
  
  // Run all classifiers in parallel
  const [ruleResult, mlResult, learnedVendorResult] = await Promise.all([
    applyRulesToTransaction(transaction).catch(() => null),
    classifyTransactionML(
      {
        description: transaction.description,
        merchant_name: transaction.merchant_name,
        amount: transaction.amount,
        transaction_date: transaction.transaction_date,
        location_id: transaction.location_id,
      },
      transaction.company_id,
      transaction.account_id
    ).catch(() => ({
      category_type: null,
      subcategory: null,
      detail_source: null,
      confidence: 0,
      method: 'none' as const,
    })),
    useLearnedVendors
      ? classifyByLearnedVendor(
          transaction.description,
          transaction.merchant_name,
          transaction.company_id
        ).catch(() => null)
      : null,
  ]);
  
  // Check learned vendor result first (highest priority from user corrections)
  if (learnedVendorResult && learnedVendorResult.confidence >= 0.85) {
    const result: ClassificationResult = {
      category_type: learnedVendorResult.category_type as any,
      subcategory: learnedVendorResult.subcategory,
      detail_source: null,
      confidence: learnedVendorResult.confidence,
      method: 'learned_vendor',
      needsReview: learnedVendorResult.confidence < 0.9,
      matchReason: 'Matched learned vendor category from previous corrections',
    };
    
    return await applyCalibration(result, transaction.company_id, useCalibration, thresholds);
  }
  
  // Combine rule and ML results
  const baseResult = combineClassifications(ruleResult, mlResult);
  
  // Apply calibration and action recommendation
  return await applyCalibration(baseResult, transaction.company_id, useCalibration, thresholds);
}

/**
 * Apply confidence calibration and determine recommended action
 */
async function applyCalibration(
  result: ClassificationResult,
  companyId: string,
  useCalibration: boolean,
  thresholds: ConfidenceThresholds
): Promise<ClassificationResult> {
  if (!useCalibration || result.confidence === 0) {
    return {
      ...result,
      calibratedConfidence: result.confidence,
      recommendedAction: determineAction(result.confidence, thresholds).action
    };
  }
  
  try {
    const calibrator = await getCalibrator(companyId);
    const calibratedConfidence = calibrator.calibrate(result.confidence);
    const actionRec = determineAction(calibratedConfidence, thresholds);
    
    return {
      ...result,
      calibratedConfidence,
      needsReview: actionRec.shouldFlag,
      recommendedAction: actionRec.action
    };
  } catch (error) {
    // If calibration fails, return uncalibrated result
    return {
      ...result,
      calibratedConfidence: result.confidence,
      recommendedAction: determineAction(result.confidence, thresholds).action
    };
  }
}

/**
 * Classifies multiple transactions in batch
 */
export async function classifyTransactions(
  transactions: TransactionToCategorize[]
): Promise<Map<string, ClassificationResult>> {
  const results = new Map<string, ClassificationResult>();
  
  // Process in batches to avoid overwhelming the system
  const batchSize = 10;
  for (let i = 0; i < transactions.length; i += batchSize) {
    const batch = transactions.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map(tx => classifyTransaction(tx))
    );
    
    batch.forEach((tx, idx) => {
      results.set(tx.id, batchResults[idx]);
    });
  }
  
  return results;
}

